import React, { useRef, useState } from "react";

import {
  findDriverCodeClash,
  normalizeDriverLastName,
  performUpdateUserReloadCarrier,
  sanitizeCarrierUser,
} from "../../../../graphql/carrier/carrierCRUD";

import { SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm } from "react-final-form";

import { logError } from "../../../../utils/appUtils";
import {
  getCarrierUserAcl,
  normalizeCarrierUserType,
} from "../../../../utils/carrierUserUtils";

export const ValidateSaveButton = ({ record, ...props }) => {
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [isSaving, setIsSaving] = useState(false);
  const capturedRef = useRef({});

  const handleValidateSave = async () => {
    const formData = form.getState().values;
    const lastNameChanged =
      normalizeDriverLastName(formData.lastName) !==
      normalizeDriverLastName(record?.lastName);
    if (record?.driverCode && lastNameChanged) {
      setIsSaving(true);
      try {
        const clash = await findDriverCodeClash(record, formData.lastName);
        if (clash) {
          notify(
            `Another driver (${clash.firstName || ""} ${clash.lastName || ""}`.trim() +
              `) already uses code ${record.driverCode} with this last name. ` +
              "Saving would lock both drivers out of code lookup at the gate.",
            "error",
          );
          setIsSaving(false);
          return; // halt submission
        }
      } catch (error) {
        // Fail open (same as email-unicity check on create): a transient query
        // error shouldn't block the manager from editing a driver.
        logError(
          "managerApp:carrierUsers:ValidateSaveButton:findDriverCodeClash",
          error,
          false,
        );
      }
      setIsSaving(false);
    }
    props.handleSubmitWithRedirect();
  };

  const transform = async (data) => {
    data = { ...data };
    const joinTableSlice = data.reloadYardCarriers?.items?.[0];
    if (joinTableSlice) {
      // Type and permissions are no longer editable here, so re-derive rather
      // than echo back: legacy short-form types get canonicalised and the stored
      // acl catches up with what the portal actually grants.
      capturedRef.current.type = normalizeCarrierUserType(joinTableSlice.type);
      capturedRef.current.acl = getCarrierUserAcl(capturedRef.current.type);
      capturedRef.current.notes = joinTableSlice.notes;
      capturedRef.current.blockAccess = joinTableSlice.blockAccess;
    }
    // Gate-enrolled drivers have no email.
    if (data.email) data.email = data.email.toLowerCase();

    const sanitized = sanitizeCarrierUser(data);
    return sanitized;
  };

  const onSuccess = async ({ data }) => {
    try {
      const reloadYardCarrier = {
        ...data.reloadYardCarriers.items[0],
        acl: capturedRef.current.acl,
        type: capturedRef.current.type,
        notes: capturedRef.current.notes,
        blockAccess: capturedRef.current.blockAccess,
      };
      await performUpdateUserReloadCarrier(reloadYardCarrier);

      notify(`User updated successfully.`);
      redirect("/carrierUsers");
    } catch (error) {
      logError(
        "managerApp:carrierUsers:ValidateSaveButton:onSuccess",
        error,
        notify,
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError(
      "managerApp:carrierUsers:ValidateSaveButton:onFailure",
      error,
      notify,
    );
  };

  return (
    <SaveButton
      {...props}
      submitOnEnter={false}
      undoable={false}
      disabled={props.pristine}
      saving={isSaving}
      handleSubmitWithRedirect={handleValidateSave}
      transform={transform}
      onSuccess={onSuccess}
      onFailure={onFailure}
    />
  );
};
